import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api.js";
import { aloneStep, stepAllowed, type AloneStep } from "@joogoos/contracts";
import { Dial } from "./chrome.js";

/**
 * **Set up, step 5** — the loop's own dials (23 §autopilot, ADR 0039).
 *
 * Read by anybody in the workspace and changed only by an owner: the loop spends this business's credits and
 * writes to strangers under its name. The page says what this box would actually let it do, rather than offering
 * a switch that would quietly do nothing.
 */

/**
 * The loop's own dials, which used to be a screen of their own.
 *
 * They are five numbers and a switch, and none of them is something a person changes twice a week — so they
 * belong at the bottom of the thing you set up once, not in the tab bar beside the work.
 */
export function Loop({ mayEdit }: { mayEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["customers", "autopilot"], queryFn: () => api.autopilot() });
  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.saveAutopilot(patch),
    onSuccess: (r) => { qc.setQueryData(["customers", "autopilot"], r); void qc.invalidateQueries({ queryKey: ["customers", "today"] }); },
  });
  const d = q.data;
  if (!d) return <p className="text-[12.5px] text-faint">…</p>;
  const s = d.switches;
  const set = (patch: Record<string, unknown>) => { if (mayEdit) save.mutate(patch); };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Dial label="Letters a week" value={String(s.lettersPerWeek)} disabled={!mayEdit}
          onChange={(v) => set({ lettersPerWeek: Number(v) || 0 })} />
        <Dial label="Credits a week" value={s.weeklyBudget} disabled={!mayEdit}
          onChange={(v) => set({ weeklyBudget: v.replace(/[^0-9.]/g, "") })} />
        <Dial label="Trying, not earning" value={`${s.exploreShare}%`} disabled={!mayEdit}
          onChange={(v) => set({ exploreShare: Number(v.replace(/[^0-9]/g, "")) || 30 })} />
      </div>
      <Alone step={aloneStep(s)} mayEdit={mayEdit} onStep={(alone) => set({ alone })} />
      <div className="flex flex-wrap items-center gap-2.5 border-t border-line pt-2.5">
        <label className="flex items-center gap-2 text-[12.5px]">
          <input type="checkbox" checked={s.sends} disabled={!mayEdit || !d.maySend} onChange={(e) => set({ sends: e.target.checked })} />
          It may send its own letters
        </label>
        <Dial label="a day, at most" value={String(s.lettersPerDay)} disabled={!mayEdit} small
          onChange={(v) => set({ lettersPerDay: Number(v) || 0 })} />
        <span className={`ml-auto px-1.5 py-0.5 rounded text-[10.5px] font-semibold ${d.maySend ? "bg-accent/10 text-accent" : "bg-line-2 text-ink-2"}`}>
          {d.gate === "rehearsal" ? "rehearsing · they all come to you"
            : d.gate === "allowlist" ? "your own addresses only"
              : d.gate === "on" ? "live — the loop still will not send" : "nothing leaves this box"}
        </span>
      </div>
      <p className="text-[11px] text-faint leading-relaxed">
        It sends only letters it wrote itself, never one you have started editing, and only while this box is
        rehearsing: turning the box live does not hand it a list of companies.
      </p>
    </div>
  );
}

/** The four steps of What Joogoos may do alone, in words (38 step 6, ADR 0068 point 5). */
const STEPS: ReadonlyArray<{ step: AloneStep; title: string; does: string }> = [
  { step: "propose", title: "Propose", does: "It writes and proposes; you say yes to everything." },
  { step: "run", title: "Run groups", does: "Inside each group's limits it changes the words a group looks for, tries a place or stops one, and keeps the version of a letter that is clearly ahead." },
  { step: "send", title: "Send", does: "On a live box it sends the letters it wrote, unedited, up to each group's number a day and inside every other limit. A group's first ten each wait a morning on Today, where you can stop it." },
  { step: "grow", title: "Grow", does: "It opens a new group from one that works, at half that group's limits and inside your weekly total." },
];

/**
 * **What Joogoos may do alone**: four steps, moved forward one at a time as trust grows, and back as far as you like
 * at once (ADR 0068 point 5, ADR 0069). Send and Grow are off until you choose them. Leaving Grow pauses the groups
 * Joogoos grew; leaving Send stops its letters going at once.
 */
function Alone({ step, mayEdit, onStep }: { step: AloneStep; mayEdit: boolean; onStep: (step: AloneStep) => void }) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1">What Joogoos may do alone</legend>
      {STEPS.map((s) => {
        const reachable = stepAllowed(step, s.step);
        return (
          <label key={s.step} className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-[12.5px] ${step === s.step ? "border-ink bg-panel" : "border-line-2 bg-page"} ${reachable ? "" : "opacity-60"}`}>
            <input type="radio" name="alone" className="mt-0.5" checked={step === s.step} disabled={!mayEdit || !reachable}
              onChange={() => onStep(s.step)} />
            <span className="flex flex-col">
              <b className="font-semibold">{s.title}</b>
              <span className="text-muted">{s.does}</span>
              {reachable ? null : <span className="text-[11px] text-faint">One step at a time: choose the one before it first.</span>}
            </span>
          </label>
        );
      })}
      <span className="text-[11px] text-faint">Retiring a group always waits for you, and so does anything you set yourself on a group's Limits.</span>
    </fieldset>
  );
}

